// Angular imports
import { formatDate } from '@angular/common';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';

// 3rd party imports
import { formatNumber } from 'devextreme/localization';
import { custom } from 'devextreme/ui/dialog';
import * as moment from 'moment-timezone';
import { cloneDeep } from 'lodash';

// Rxjs imports
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

// Environment imports
import { environment } from '../../../environments/environment';

// CC-Common imports
import { CCLocalStorageCategory, CCLocalStorageService, IconConstants, Utils } from 'cc-common';

// Service imports
import { ContextManagerService } from '../data-services/context-manager.service';
import { PrecisionService } from './precision.service';
import { ServerErrorService } from './server-error.service';

// Model imports
import { IGridColumnFilter } from '../../_models/grid-column-filter.interface';
import { NavigationInfo } from '../../_models/navigation-info';
import { PopMenuInfo } from '../../_models/pop-menu-info';
import { SideNavTreeInfo } from '../../_models/side-nav-tree-info';
import { GLJournalAttachmentRawDto } from '../../cirabooks/shared-cirabooks-module/_models/general-ledger/journal-entry/gl-journal-attachment-raw-dto';

// Constant imports
import { LotUnitConstants } from '../../lotunit-tools-module/_constants/lotunit-constants';
import { Constants } from '../../_constants/constants';
import { SideNavigationTree } from '../../_constants/side-navigation-tree';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { StringFormatConstants } from '../../_constants/string-format-constants';
import { ValidationFormatConstants } from '../../_constants/validation-format-constants';
import { ActionConstants } from '../../_constants/action-constants';

@Injectable({
    providedIn: 'root'
})
export class HelperService {
    subjectAutoSelectHomeMenuItem = new Subject();
    subjectSideNavSizeChanged = new Subject();
    subjectLaunchPreferences = new Subject();
    subjectLaunchPortalAnnouncements = new Subject();
    subjectLaunchResidentAnnouncements = new Subject();
    subjectNavigationChange = new Subject();
    subjectFullScreenWidget = new Subject();

    constructor(
        private readonly httpClient: HttpClient,
        private readonly sanitizer: DomSanitizer,
        private readonly router: Router,
        private readonly localStorageService: CCLocalStorageService,
        private readonly contextService: ContextManagerService,
        private readonly precisionService: PrecisionService,
        private readonly errorService: ServerErrorService
    ) {}

    isValidCustomerCode(code: string): boolean {
        if (!!code) {
            return ValidationFormatConstants.rnumPattern.test(code);
        }
        return false;
    }

    isProduction(): boolean {
        return environment.production;
    }

    isLocalhost(): boolean {
        const url = window.location.href;
        return url.indexOf('localhost') >= 0;
    }

    isOpenLinksExternal(): boolean {
        const choice =
            this.localStorageService.getItem(StorageKeyConstants.userOpenLinksKey, CCLocalStorageCategory.PREF) ||
            environment.defaultOpenLinks;
        return choice.toLowerCase() !== 'internal';
    }

    showPreferences(): void {
        this.subjectLaunchPreferences.next(true);
    }

    hidePreferences(): void {
        this.subjectLaunchPreferences.next(false);
    }

    getDateString(myDate: Date): string {
        return !!myDate ? formatDate(myDate, StringFormatConstants.dateFormat, Constants.localeEnglishUS) : '';
    }

    getCurrencyStringFormat(value: number): string {
        return !!value ? formatNumber(value, this.precisionService.getCurrencyFormat()) : '';
    }

    getRoundedCurrencyStringFormat(value: number): string {
        return !!value ? formatNumber(Math.round(value), this.precisionService.getCurrencyFormat(0)) : '';
    }

    getOkDialog(title: string, messageHtml: string, buttonText = 'OK', buttonType: any = 'default') {
        return custom({
            title,
            messageHtml,
            buttons: [
                {
                    type: buttonType,
                    text: buttonText,
                    hint: buttonText,
                    onClick: (e) => {
                        return true;
                    }
                }
            ]
        });
    }

    getConfirmationDialog(
        title: string,
        messageHtml: string,
        yesButtonTitle = 'Yes',
        noButtonTitle = 'No',
        yesButtonType: any = 'default',
        noButtonType: any = 'default'
    ) {
        return custom({
            title,
            messageHtml,
            buttons: [
                {
                    text: noButtonTitle,
                    type: noButtonType,
                    onClick: (e) => {
                        return false;
                    }
                },
                {
                    text: yesButtonTitle,
                    type: yesButtonType,
                    onClick: (e) => {
                        return true;
                    }
                }
            ]
        });
    }

    getAgreementDialog(
        title: string,
        messageHtml: string,
        yesButtonTitle = 'Yes',
        noButtonTitle = 'No',
        cancelButtonTitle = 'Cancel',
        yesButtonType: any = 'default',
        noButtonType: any = 'default',
        cancelButtonType: any = 'default'
    ) {
        return custom({
            title,
            messageHtml,
            buttons: [
                {
                    text: yesButtonTitle,
                    type: yesButtonType,
                    onClick: (e) => {
                        return true;
                    }
                },
                {
                    text: noButtonTitle,
                    type: noButtonType,
                    onClick: (e) => {
                        return false;
                    }
                },
                {
                    text: cancelButtonTitle,
                    type: cancelButtonType,
                    onClick: (e) => {
                        return null;
                    }
                }
            ]
        });
    }

    getDateTimeString(myDate: Date): string {
        return formatDate(myDate, StringFormatConstants.dateTimeFormat, Constants.localeEnglishUS);
    }

    getUserTimezone(): string {
        return (
            this.localStorageService.getItem(StorageKeyConstants.userTimeZoneKey, CCLocalStorageCategory.PREF) ||
            moment.tz.guess()
        );
    }

    openExternalEmailApp(emailId: string): void {
        const link = document.createElement('a');
        link.style.display = 'none';
        link.target = '_blank';
        link.href = `mailto:${emailId}`;
        document.body.appendChild(link);
        link.click();
        // Remove link & Revoke object URL
        setTimeout(() => document.body.removeChild(link), 500);
    }

    downloadFileToSystem(fileUrl: string, fileName: string): void {
        const link = document.createElement('a');
        link.style.display = 'none';
        link.target = '_blank';
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        // Remove link & Revoke object URL
        setTimeout(() => document.body.removeChild(link), 500);
    }

    showFileInBrowserTab(fileUrl: string, fileName: string): void {
        const choice = this.localStorageService.getItem(
            StorageKeyConstants.userDownloadsKey,
            CCLocalStorageCategory.PREF
        );

        if (choice === 'browsertab') {
            if (this.isViewableMimeType(fileName)) {
                const win = window.open(fileUrl, '_blank');
                try {
                    if (Utils.notNullAndDefined(win)) {
                        win.focus();
                    }
                } catch (e) {
                    const dialog = this.getOkDialog(
                        'Error',
                        'Looks like Pop-up Blocker is enabled! Please add ciranet.com domain to the exception list.'
                    );
                    dialog.show();
                }
                setTimeout(() => {
                    try {
                        // Possible DOMException in Firefox while accessing win.document.
                        // [DEV-4034]: "Permission denied to access property" will occure on Firefox browsers due to
                        // violation the same-origin policy as blob urls will be treated differently
                        if (!!win && !!win.document) {
                            win.document.title = fileName;
                        }
                    } catch (e) {
                        // Ignore this firefox error.
                    }
                }, 1000);
            } else {
                this.downloadFileToSystem(fileUrl, fileName);
            }
        } else {
            this.downloadFileToSystem(fileUrl, fileName);
        }
    }

    showBlobInBrowserTab(fileData: Blob, fileName: string): void {
        const blob = new Blob([fileData], { type: this.getMimeTypeByExtension(fileName) });
        const fileUrl = window.URL.createObjectURL(blob);
        this.showFileInBrowserTab(fileUrl, fileName);
        // Remove link & Revoke object URL
        setTimeout(() => window.URL.revokeObjectURL(fileUrl), 1000);
    }

    downloadFileUsingBlob(fileData: Blob, fileName: string): void {
        const blob = new Blob([fileData], { type: this.getMimeTypeByExtension(fileName) });
        const fileUrl = window.URL.createObjectURL(blob);
        const choice = this.localStorageService.getItem(
            StorageKeyConstants.userDownloadsKey,
            CCLocalStorageCategory.PREF
        );

        if (choice === 'browsertab') {
            this.showFileInBrowserTab(fileUrl, fileName);
        } else {
            this.downloadFileToSystem(fileUrl, fileName);
        }
        // Remove link & Revoke object URL
        setTimeout(() => window.URL.revokeObjectURL(fileUrl), 2500);
    }

    downloadFileUsingServiceMethod(downloadMethod: Observable<Blob>, fileName: string): Observable<boolean> {
        return new Observable<boolean>((observer) => {
            downloadMethod.subscribe(
                (fileData: Blob) => {
                    this.downloadFileUsingBlob(fileData, fileName);
                    observer.next(true);
                    observer.complete();
                },
                (error) => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    downloadFileUsingUrl(fileUrl: string, fileName: string, callback?: () => void): void {
        this.getImageBlobFromUrl(fileUrl).subscribe(
            (fileData: Blob) => {
                this.downloadFileUsingBlob(fileData, fileName);
                if (!!callback) {
                    callback();
                }
            },
            (error) => {
                if (!!callback) {
                    callback();
                }
                this.errorService.displayError(error);
            }
        );
    }

    downloadFileUsingBase64(fileBase64: string, fileName: string): void {
        const blob: Blob = this.getFileBlobFromBase64(fileBase64, fileName);
        this.downloadFileUsingBlob(blob, fileName);
    }

    getFileUrlFromBlob(fileData: Blob): string {
        const blob: Blob = new Blob([fileData], { type: 'application/pdf' });
        return window.URL.createObjectURL(blob);
    }

    getFileBlobFromBase64(fileBase64: string, fileName: string): Blob {
        const byteCharacters = atob(fileBase64);
        const chunkSize = 512;
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += chunkSize) {
            const slice = byteCharacters.slice(offset, offset + chunkSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        const mimeType = this.getMimeTypeByExtension(fileName);
        return new Blob(byteArrays, { type: mimeType });
    }

    getImageBlobFromUrl(url: string): Observable<Blob> {
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getImageSafeUrlFromBlob(imageData: Blob): SafeUrl {
        const imageUrl = window.URL.createObjectURL(imageData);
        return this.sanitizer.bypassSecurityTrustUrl(imageUrl);
    }

    getImageSafeUrlFromUrl(url: string): Observable<string | SafeUrl> {
        return this.getImageBlobFromUrl(url).pipe(map((imageData: Blob) => this.getImageSafeUrlFromBlob(imageData)));
    }

    getPdfSafeUrlFromUrl(url: string): Observable<string | SafeUrl> {
        return this.getImageBlobFromUrl(url).pipe(map((imageData: Blob) => this.getPdfSourceFromBlob(imageData)));
    }

    getPdfSourceFromBlob(fileData: Blob): SafeResourceUrl {
        const fileUrl = this.getFileUrlFromBlob(fileData);
        return this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl);
    }

    isFromDateGreaterThenToDate(fromDate: Date, toDate: Date): boolean {
        const beginDate = moment(fromDate);
        const endDate = moment(toDate);
        return endDate.isAfter(beginDate);
    }

    changeNavigation(url: string): void {
        this.subjectNavigationChange.next(url);
    }

    sortSequenceItems(list: Array<any>): Array<any> {
        return (list || []).sort((x, y) => {
            x.sequence = !!x.sequence ? x.sequence : 9999;
            y.sequence = !!y.sequence ? y.sequence : 9999;
            if (x.sequence > y.sequence) {
                return 1;
            } else if (x.sequence < y.sequence) {
                return -1;
            }
            return 0;
        });
    }

    isViewableMimeType(fileName: string): boolean {
        let isViewable = false;
        if (!!fileName && fileName.trim().length > 0 && fileName.trim().indexOf('.') !== -1) {
            const fileExt = fileName
                .trim()
                .toLowerCase()
                .substring(fileName.lastIndexOf('.') + 1);
            isViewable = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'csv', 'txt'].indexOf(fileExt) !== -1;
        }
        return isViewable;
    }

    getFileIconByExtension(fileName: string): string {
        let icon = 'fas fa-file-alt';
        if (!!fileName && fileName.trim().length > 0 && fileName.trim().indexOf('.') !== -1) {
            const fileExt = fileName
                .trim()
                .toLowerCase()
                .substring(fileName.lastIndexOf('.') + 1);
            if (fileExt === 'pdf') {
                icon = 'far fa-file-pdf cc-icon-pdf';
            } else if (['doc', 'docx'].indexOf(fileExt) !== -1) {
                icon = 'fas fa-file-word cc-icon-docx';
            } else if (['xls', 'xlsx'].indexOf(fileExt) !== -1) {
                icon = 'fas fa-file-excel cc-icon-xlsx';
            } else if (['ppt', 'pptx'].indexOf(fileExt) !== -1) {
                icon = 'fas fa-file-powerpoint cc-icon-pptx';
            } else if (['jpg', 'jpeg'].indexOf(fileExt) !== -1) {
                icon = 'fas fa-image cc-icon-jpg';
            } else if (['png'].indexOf(fileExt) !== -1) {
                icon = 'fas fa-image cc-icon-png';
            } else if (['gif'].indexOf(fileExt) !== -1) {
                icon = 'fas fa-image cc-icon-gif';
            } else if (['bmp'].indexOf(fileExt) !== -1) {
                icon = 'fas fa-image cc-icon-gif';
            } else if (['zip'].indexOf(fileExt) !== -1) {
                icon = 'fas fa-file-archive cc-icon-ppt';
            } else if (['rar'].indexOf(fileExt) !== -1) {
                icon = 'fas fa-file-archive cc-icon-ppt';
            }
        }
        return icon;
    }

    getIconByIndicatorName(data: any): string {
        let icon = '';
        if (Utils.notNullAndDefined(data)) {
            if (Utils.equalsIgnoreCase(data.statusIndicator, 'Info')) {
                icon = `${IconConstants.INFO} dx-theme-accent-as-text-color`;
            } else if (Utils.equalsIgnoreCase(data.statusDatatype, 'BOOLEAN')) {
                if (Utils.equalsIgnoreCase(data.statusIndicator, 'Red')) {
                    icon = Utils.equalsIgnoreCase(data.statusDisplayText, 'In Collections')
                        ? `${IconConstants.FLAG} cc-icon-cross`
                        : `${IconConstants.CROSS} cc-icon-cross`;
                }
                if (Utils.equalsIgnoreCase(data.statusIndicator, 'Green')) {
                    icon = `${IconConstants.CHECK} cc-icon-check`;
                }
            }
        }
        return icon;
    }

    getIconByKey(key: string): string {
        let icon = '';
        if (Utils.notNullOrEmpty(key)) {
            if (Utils.equalsIgnoreCase(key, 'OK')) {
                icon = `${IconConstants.CHECK} cc-icon-check`;
            }
            if (Utils.equalsIgnoreCase(key, 'ALERT')) {
                icon = `${IconConstants.WARN} cc-text-warned`;
            }
            if (Utils.equalsIgnoreCase(key, 'INFO')) {
                icon = `${IconConstants.INFO} cc-icon-content`;
            }
            if (Utils.equalsIgnoreCase(key, 'FLAG_RED')) {
                icon = `${IconConstants.FLAG} cc-icon-pdf`;
            }
            if (Utils.equalsIgnoreCase(key, 'GREEN_ARROW')) {
                icon = `${IconConstants.ARROW_UP} cc-icon-check`;
            }
            if (Utils.equalsIgnoreCase(key, 'RED_ARROW')) {
                icon = `${IconConstants.ARROW_DOWN} cc-icon-cross`;
            }
            if (Utils.equalsIgnoreCase(key, 'YELLOW_ARROW')) {
                icon = `${IconConstants.ARROW_RIGHT} cc-icon-ppt`;
            }
        }
        return icon;
    }

    getMimeTypeByExtension(fileName: string): string {
        let mimeType = 'application/octet-stream';
        if (!!fileName && fileName.trim().length > 0 && fileName.trim().indexOf('.') !== -1) {
            const fileExt = fileName
                .trim()
                .toLowerCase()
                .substring(fileName.lastIndexOf('.') + 1);
            switch (fileExt) {
                case 'pdf':
                    mimeType = 'application/pdf';
                    break;
                case 'doc':
                    mimeType = 'application/msword';
                    break;
                case 'docx':
                    mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                    break;
                case 'xls':
                    mimeType = 'application/vnd.ms-excel';
                    break;
                case 'xlsx':
                    mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                    break;
                case 'ppt':
                    mimeType = 'application/vnd.ms-powerpoint';
                    break;
                case 'pptx':
                    mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
                    break;
                case 'jpg':
                case 'jpeg':
                    mimeType = 'image/jpeg';
                    break;
                case 'png':
                case 'gif':
                case 'bmp':
                    mimeType = `image/${fileExt}`;
                    break;
                case 'csv':
                    mimeType = 'text/csv';
                    break;
                case 'txt':
                    mimeType = 'text/plain';
                    break;
                case 'zip':
                    mimeType = 'application/zip';
                    break;
                case '7z':
                    mimeType = 'application/x-7z-compressed';
                    break;
            }
        }
        return mimeType;
    }

    navigateToRoute(info: NavigationInfo, context?: 'property' | 'community', openInTab = false): void {
        if (!!info.navCode) {
            const menu = this.findSideNavTreeInfo(info.navCode, context);
            if (!!menu && !!menu.path) {
                let url = this.getUrlForCurrentContext(menu.path);
                const queryParams = !!info.view ? { queryParams: { view: info.view } } : undefined;
                if (openInTab) {
                    url = !!info.view ? `${url}?view=${info.view}` : url;
                    window.open(url, '_blank').focus();
                } else {
                    this.router.navigate([url], queryParams);
                }
            }
        }
    }

    getDefaultContextPath(communityId: number = null, customerId: string = null): string {
        let path = '';
        if (customerId && communityId) {
            path = `/property/${communityId}/${customerId}/home`;
        } else if (communityId) {
            path = `/community/${communityId}/home`;
        }
        return path;
    }

    getUrlForCurrentContext(path: string): string {
        const context = this.contextService.getSelectedContext();
        let url: string;
        if (!!context) {
            if (
                path.indexOf(Constants.communityPathPattern) !== -1 ||
                path.indexOf(Constants.propertyPathPattern) !== -1
            ) {
                let newpath = '';
                if (path.indexOf(Constants.communityPathPattern) !== -1) {
                    newpath = path.replace(Constants.communityPathPattern, context.communityId.toString());
                }

                if (path.indexOf(Constants.propertyPathPattern) !== -1) {
                    newpath = newpath
                        ? newpath.replace(Constants.propertyPathPattern, context.customerId)
                        : path.replace(Constants.propertyPathPattern, context.customerId);
                }
                url = newpath;
            } else {
                url = path;
            }
        } else if (
            path.indexOf(Constants.communityQueryPattern) !== -1 ||
            path.indexOf(Constants.propertyQueryPattern) !== -1
        ) {
            url = path
                .replace(`${Constants.communityQueryPattern}`, '')
                .replace(`${Constants.propertyQueryPattern}`, '');
        } else if (
            path.indexOf(Constants.communityPathPattern) !== -1 ||
            path.indexOf(Constants.propertyPathPattern) !== -1
        ) {
            url = path
                .replace(`/${Constants.communityPathPattern}`, '')
                .replace(`/${Constants.propertyPathPattern}`, '');
        } else {
            url = path;
        }

        return !!url && url.startsWith('/') ? url.replace('/', '') : url;
    }

    getFileSizeDisplayText(size: number): string {
        let text = `${size}B`;
        if (size > 1024) {
            text = `${(size / 1024.0).toFixed(2)}KB`;
        }
        if (size > 1024 * 1024) {
            text = `${(size / (1024.0 * 1024.0)).toFixed(2)}MB`;
        }
        return text;
    }

    openDefaultPropertyPageInNewWindow(communityId: number, customerId: string, page = 'home', queryString = '') {
        let url = `property/${communityId}/${customerId}/${page}`;
        if (!!queryString && queryString.length > 0) {
            url = `property/${communityId}/${customerId}/${page}?${queryString}`;
        }
        this.openWindow(url, '_blank', null, null);
    }

    openDefaultCommunityPageInNewWindow(communityId: number, page = 'home', queryString = '') {
        let url = `community/${communityId}/${page}`;
        if (!!queryString && queryString.length > 0) {
            url = `community/${communityId}/${page}?${queryString}`;
        }
        this.openWindow(url, '_blank', null, null);
    }

    openDefaultVendorManagementPageInNewWindow(queryString?: string) {
        let url = `my-portfolio/vendor-management`;
        if (Utils.notNullAndDefined(queryString)) {
            url = `my-portfolio/vendor-management?${queryString}`;
        }
        this.openWindow(url, '_blank', null, null);
    }

    openDefaultCorpVendorManagementPageInNewWindow(queryString?: string) {
        let url = `ccb/ap/vendormanagement`;
        if (Utils.notNullAndDefined(queryString)) {
            url = `ccb/ap/vendormanagement?${queryString}`;
        }
        this.openWindow(url, '_blank', null, null);
    }

    openDefaultCorpCustomerManagementPageInNewWindow(queryString?: string) {
        let url = `ccb/ar/customer-management`;
        if (Utils.notNullAndDefined(queryString)) {
            url = `ccb/ar/customer-management?${queryString}`;
        }
        this.openWindow(url, '_blank', null, null);
    }

    openCorpCustomerContractPageInNewWindow(queryString?: string) {
        let url = `ccb/rm/customercontracts`;
        if (Utils.notNullAndDefined(queryString)) {
            url = `ccb/rm/customercontracts?${queryString}`;
        }
        this.openWindow(url, '_blank', null, null);
    }

    openPropertyPageInNewWindow(
        communityId: number,
        customerId: string,
        page: string,
        features = 'width=1366px,height=768px,toolbar=0,location=0,menubar=0'
    ) {
        const url = `property/${communityId}/${customerId}/${page}`;
        const win: any = window.open(`${url}`, '_blank', features);
        if (Utils.notNullAndDefined(win)) {
            win.focus();
        }
    }

    openCommunityPageInNewWindow(
        communityId: number,
        page: string,
        features = 'width=1366px,height=768px,toolbar=0,location=0,menubar=0'
    ) {
        const url = `community/${communityId}/${page}`;
        const win: any = window.open(`${url}`, '_blank', features);
        if (Utils.notNullAndDefined(win)) {
            win.focus();
        }
    }

    openDefaultBoardApprovalPageInNewWindow(queryString?: any) {
        let url = `my-work/board-approval`;
        if (Utils.notNullAndDefined(queryString)) {
            url = `my-work/board-approval?${queryString}`;
        }
        this.openWindow(url, '_blank', null, null);
    }

    getHyperlinkText(text: string): string {
        return text.replace(ValidationFormatConstants.webUrlPattern, (url) => {
            return `<a target="_blank" href="${url}">${url}</a>`;
        });
    }

    managePhoneAndEmail(): void {
        const url = this.getUrlForCurrentContext(
            `property/${Constants.communityPathPattern}/${Constants.propertyPathPattern}/manage-phone-email`
        );
        const params = `?readOnly=true`;
        window.open(
            `${url}${params}`,
            '_blank',
            'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=false'
        );
    }

    logAPhoneCall(): void {
        const url = this.getUrlForCurrentContext(
            `tools/${Constants.communityPathPattern}/logphonecall?customerId=${Constants.propertyPathPattern}`
        );
        window.open(
            `${url}`,
            '_blank',
            `directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=true,width=${LotUnitConstants.logCallPopupWindowWidth},height=${LotUnitConstants.logCallPopupWindowHeight}`
        );
    }

    calculateTotalSummary(e): void {
        switch (e.summaryProcess) {
            case 'start':
                e.totalValue = 0;
                break;
            case 'calculate':
                const value = e.value[e.name] || e.value;
                if (!!value && typeof value === 'number') {
                    const balance = this.precisionService.getRoundedNumber(value);
                    e.totalValue += balance;
                }
                break;
            case 'finalize':
                e.totalValue = this.precisionService.getRoundedNumber(e.totalValue);
                break;
        }
    }

    prepareMenuItems(items: SideNavTreeInfo[]): PopMenuInfo[] {
        return items.map((item) => {
            const info: PopMenuInfo = new PopMenuInfo();
            info.id = item.id;
            info.parentId = item.parentId;
            info.icon = item.icon ? item.icon : null;
            info.name = item.name;
            info.text = item.text;
            info.path = item.path ? item.path : null;
            info.selected = item.selected;
            info.isInternal = item.isInternal;
            info.isSpecificRole = item.isSpecificRole;
            info.isWorkspace = item.isWorkspace;
            info.items = [];
            info.hasAccess = item.hasAccess;
            if (!!item.items && item.items.length > 0) {
                info.items = this.prepareMenuItems(item.items);
            }
            return info;
        });
    }

    sortByMultipleFields(items: any[], opts: { field: string; direction: number }[]): any[] {
        const sortFunction = (a: any, b: any) => {
            let i = 0;
            let result = 0;
            while (i < opts.length && result === 0) {
                let multiplier = 0;
                if (a[opts[i].field].toString() < b[opts[i].field].toString()) {
                    multiplier = -1;
                } else if (a[opts[i].field].toString() > b[opts[i].field].toString()) {
                    multiplier = 1;
                }
                result = opts[i].direction * multiplier;
                i++;
            }
            return result;
        };
        return items.sort(sortFunction);
    }

    openSearchInvoice(communityId: number, vendorId: number): void {
        let url = `workflow/wfinvoices?communityId=${communityId}`;
        url += `&vendorId=${vendorId}&mode=popup&showLimitedFilters=true&sourceType=All`;

        const win = window.open(url, '_blank', 'scrollbars=1,resizable=no');
        if (Utils.notNullAndDefined(win)) {
            win.focus();
        }
    }

    findSideNavTreeInfo(navCode: string, context?: string): SideNavTreeInfo {
        // Method to validate condition
        const isValidNavItem = (item: SideNavTreeInfo) =>
            Utils.equalsIgnoreCase(item.name, navCode) ||
            (Utils.notNullAndDefined(item.path) && Utils.equalsIgnoreCase(item.path, navCode));
        // Method to find in parent and child collections
        const findInMenuItems = (items: SideNavTreeInfo[]) => {
            const flatItems = SideNavigationTree.flattenSideNavTreeItems(items);
            return flatItems.find((item) => isValidNavItem(item));
        };

        let navInfo = null;
        if (context === 'property') {
            navInfo = findInMenuItems(
                SideNavigationTree.propertyTreeItems
                    .concat(SideNavigationTree.hiddenPropertyItems)
                    .concat(SideNavigationTree.communityTreeItems)
            );
        } else if (context === 'community') {
            navInfo = findInMenuItems(SideNavigationTree.communityTreeItems);
        } else if (!context) {
            navInfo = findInMenuItems(SideNavigationTree.masterTreeItems);
        }
        return navInfo;
    }

    showUrlInBrowserTab(fileUrl: string, fileName: string): Observable<void> {
        return new Observable((observer) => {
            this.httpClient.get(fileUrl, { observe: 'response', responseType: 'arraybuffer' }).subscribe(
                (resp: HttpResponse<ArrayBuffer>) => {
                    const headers = resp.headers;
                    const type = headers.get('content-type');
                    const blob = new Blob([resp.body], { type });
                    const blobUrl = window.URL.createObjectURL(blob);
                    const win = window.open(blobUrl, '_blank');
                    try {
                        if (Utils.notNullAndDefined(win)) {
                            win.focus();
                        }
                    } catch (e) {
                        const dialog = this.getOkDialog(
                            'Error',
                            'Looks like Pop-up Blocker is enabled! Please add ciranet.com domain to the exception list.'
                        );
                        dialog.show();
                    }
                    setTimeout(() => {
                        try {
                            // Possible DOMException in Firefox while accessing win.document.
                            // [DEV-4034]: "Permission denied to access property" will occure on Firefox browsers due to
                            // violation the same-origin policy as blob urls will be treated differently
                            if (!!win && !!win.document) {
                                win.document.title = fileName;
                            }
                        } catch (e) {
                            // Ignore this firefox error.
                        }
                    }, 1000);
                    // Remove link & Revoke object URL
                    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2500);
                    observer.next();
                    observer.complete();
                },
                (error) => {
                    observer.error(error);
                    observer.complete();
                }
            );
        });
    }

    prepareUniqueResponsibleUsers(dataSource: any[], key: string): any[] {
        const uniqueResponsibleUsers = [];
        if (Utils.notNullOrEmpty(dataSource)) {
            dataSource.forEach((row) => {
                if (Utils.notNullOrEmpty(row[key])) {
                    row[key].forEach((user) => {
                        if (!uniqueResponsibleUsers.find((d) => Utils.equals(user.id, d.user.id))) {
                            uniqueResponsibleUsers.push({
                                text: user.displayName,
                                value: row[key].map((d) => d.displayName).join(', '),
                                user
                            });
                        }
                    });
                }
            });
        }

        return uniqueResponsibleUsers;
    }

    prepareUniqueIds(dataSource: any[], key: string): any[] {
        const uniqueIds = [];
        if (Utils.notNullOrEmpty(dataSource)) {
            dataSource.forEach((row) => {
                if (Utils.notNullOrEmpty(row[key])) {
                    row[key].forEach((wf) => {
                        if (!uniqueIds.find((d) => Utils.equals(wf, d.wf))) {
                            uniqueIds.push({
                                text: wf,
                                value: row[key].join(', '),
                                wf
                            });
                        }
                    });
                } else {
                    if (!uniqueIds.find((d) => Utils.isNullOrUndefined(d.value))) {
                        uniqueIds.push({
                            text: '(Blanks)',
                            value: null
                        });
                    }
                }
            });
        }

        return uniqueIds;
    }

    getGridColumnFilterRecords<T>(
        dataSource: Array<any>,
        field: string,
        keyExpr: (rec: T) => string,
        displayExpr: (rec: T) => string
    ): Array<IGridColumnFilter<T>> {
        const uniqueRecords = new Map<string, IGridColumnFilter<T>>();
        if (Utils.notNullOrEmpty(dataSource)) {
            dataSource.forEach((item: any) => {
                const records = item[field] as Array<T>;
                if (Utils.notNullOrEmpty(records)) {
                    records.forEach((rec) => {
                        const key = keyExpr(rec);
                        if (Utils.isFalse(uniqueRecords.has(key))) {
                            uniqueRecords.set(key, {
                                text: displayExpr(rec),
                                value: records.map((d) => displayExpr(d)).join(', '),
                                record: rec
                            });
                        }
                    });
                } else {
                    uniqueRecords.set(null, {
                        text: '(Blank)',
                        value: '',
                        record: null
                    });
                }
            });
        }

        return Array.from(uniqueRecords.values());
    }

    convertHTMLToText(html: string): string {
        const tempDivElement = document.createElement('div');
        tempDivElement.innerHTML = html;
        return tempDivElement.textContent || tempDivElement.innerText || '';
    }

    hasChanges(data1: any, data2: any): boolean {
        if (data1 === data2) {
            return false;
        }

        if (typeof data1 !== 'object' || Utils.isNull(data1) || typeof data2 !== 'object' || Utils.isNull(data2)) {
            return true;
        }

        if (data1 instanceof Date && data2 instanceof Date) {
            return data1.getTime() !== data2.getTime();
        }

        const keys1 = Object.keys(data1);
        const keys2 = Object.keys(data2);

        if (Utils.notEquals(keys1.length, keys2.length)) {
            return true;
        }

        for (const key of keys1) {
            if (Utils.isFalse(keys2.includes(key)) || Utils.isTrue(this.hasChanges(data1[key], data2[key]))) {
                return true;
            }
        }

        return false;
    }

    hasEditableAttachments(attachments: GLJournalAttachmentRawDto[]): boolean {
        // ANG-2378: Checks if there is more than one attachment without a DELETE crud action
        const nonDeletedAttachments = attachments.filter((attachment) =>
            Utils.notEqualsIgnoreCase(attachment.crudAction, ActionConstants.DELETE)
        );
        return Utils.isTrue(nonDeletedAttachments.length > 1);
    }

    // check if 2 objects are equal with excluded properties
    isEqual(obj1: any, obj2: any, excludedKeys: string[] = []): boolean {
        if (obj1 === obj2) {
            return true;
        }

        if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
            return false;
        }

        const keys1 = Object.keys(obj1 as object).filter((key) => !excludedKeys.includes(key));
        const keys2 = Object.keys(obj2 as object).filter((key) => !excludedKeys.includes(key));

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            if (!keys2.includes(key) || !this.isEqual((obj1 as any)[key], (obj2 as any)[key], excludedKeys)) {
                return false;
            }
        }

        return true;
    }

    // get a list of different properties between 2 objects excluding excluded properties
    getDifferentProperties(o1: any, o2: any, currentPath: string, excludedKeys: string[] = []): string[] {
        const differences: string[] = [];

        if (o1 === o2) {
            return differences;
        }

        if (typeof o1 !== 'object' || o1 === null || typeof o2 !== 'object' || o2 === null) {
            differences.push(this.getPropertyNameFromOverridenField(currentPath));
            return differences;
        }

        const keys1 = Object.keys(o1).filter((key) => !excludedKeys.includes(key));
        const keys2 = Object.keys(o2).filter((key) => !excludedKeys.includes(key));

        if (keys1.length !== keys2.length) {
            differences.push(this.getPropertyNameFromOverridenField(currentPath));
            return differences;
        }

        for (const key of keys1) {
            const newPath = currentPath ? `${currentPath}.${key}` : key;

            if (!keys2.includes(key)) {
                differences.push(this.getPropertyNameFromOverridenField(newPath));
            } else {
                const childDifferences = this.getDifferentProperties(o1[key], o2[key], newPath, excludedKeys);
                differences.push(...childDifferences);
            }
        }

        return differences;
    }

    // check if there is a difference between two properties of object 1 and object 2, we will copy object_2's value to object_1's property
    deepCopyDifferences(obj1: any, obj2: any, excludedKeys: string[] = [], path: string = ''): any {
        // Check if either obj1 or obj2 is not an object or is null, in which case return obj1
        if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
            return obj1;
        }
        const keys = Object.keys(obj2 as object).filter((key) => !excludedKeys.includes(key));
        // Create a deep copy of obj1 using cloneDeep to avoid sonar error of to not always return the same value.
        const modifiedObj = cloneDeep(obj1);

        // Loop through the filtered keys of obj2
        for (const key of keys) {
            const val1 = (obj1 as any)[key];
            const val2 = (obj2 as any)[key];
            const currentPath = path ? `${path}.${key}` : key;

            if (typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null) {
                (modifiedObj as any)[key] = this.deepCopyDifferences(val1, val2, excludedKeys, currentPath);
            } else if (val1 !== val2) {
                (modifiedObj as any)[key] = val2;
            }
        }
        // Return the modified object, which now contains the differences between obj1 and obj2
        return modifiedObj;
    }

    getPropertyNameFromOverridenField(path: string): string {
        const parts = path.split('.');

        if (parts.length >= 2) {
            const target = parts[parts.length - 2];

            // Add space between words and capitalize the first letter of each word
            return target.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize each word
        }
        return path;
    }

    createACase(communityId: number, homewonerId: string): void {
        const url = this.getUrlForCurrentContext(`tools/${communityId}/createcase?customerId=${homewonerId}`);
        window.open(
            `${url}`,
            '_blank',
            `directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=true,width=${LotUnitConstants.logCallPopupWindowWidth},height=${LotUnitConstants.logCallPopupWindowHeight}`
        );
    }

    openWindow(
        url: string,
        target: string = '_blank',
        windowWidth: number,
        windowHeight: number,
        scrollbars: boolean = false
    ): void {
        const scrollbarsOption = scrollbars ? 'scrollbars=1,' : '';
        const width = Utils.notNullAndDefined(windowWidth) ? `width=${windowWidth},` : '';
        const height = Utils.notNullAndDefined(windowHeight) ? `height=${windowHeight},` : '';
        const windowOptions = `${scrollbarsOption}${width}${height}`;
        const newWindow = window.open(url, target, windowOptions);

        if (Utils.notNullAndDefined(newWindow)) {
            newWindow.focus();
        }
    }

    openWindowWithContent(
        target: string = '_blank',
        windowWidth: number,
        windowHeight: number,
        scrollbars: boolean = false,
        htmlContent: string
    ): void {
        const scrollbarsOption = scrollbars ? 'scrollbars=1,' : '';
        const width = Utils.notNullAndDefined(windowWidth) ? `width=${windowWidth},` : '';
        const height = Utils.notNullAndDefined(windowHeight) ? `height=${windowHeight},` : '';
        const windowOptions = `${scrollbarsOption}${width}${height}`;

        // Open the window
        const newWindow = window.open('', target, windowOptions);

        if (Utils.notNullAndDefined(newWindow)) {
            // Focus the window
            newWindow.focus();

            // Timeout mechanism to prevent infinite loop
            const maxWaitTime = 3000; // Maximum wait time in milliseconds (3 seconds)
            const startTime = Date.now();

            // Polling to ensure document readiness using readyState
            const intervalId = setInterval(() => {
                const elapsedTime = Date.now() - startTime;

                if (elapsedTime > maxWaitTime) {
                    // Timeout: Unable to write content to the new window.
                    clearInterval(intervalId);
                    return;
                }

                if (
                    Utils.notNullAndDefined(newWindow.document) &&
                    Utils.equalsIgnoreCase(newWindow.document.readyState, 'complete')
                ) {
                    clearInterval(intervalId);

                    // Write content to the new window
                    newWindow.document.open();
                    newWindow.document.write(htmlContent);
                    newWindow.document.close();
                }
            }, 50); // Poll every 50ms
        }
    }
}
